import { IAtRiskOverridePatient } from "../components/CustomComponents/AtRiskOverrideModal";
import { featureFlag } from "../constants/feature";

const AT_RISK_REVIEW_LOCK_BYPASS_FEATURE = "at_risk_review_lock_bypass";

const normalizeEmail = (email?: string | null): string =>
  email?.trim().toLowerCase() ?? "";

/**
 * Detects the VRN home-health restricted Navigator role. Returns true when ALL
 * of these are met:
 *   - The currently selected clinic has `clinic_type === "homehealth"`.
 *   - The user is in the `setup_app_user` Cognito group.
 *   - The user is in the `VRN` Cognito group.
 *
 * This is the single source of truth for the restricted view. Every other
 * piece of the feature (LeftMenu restriction, HomeHealthRouteGuard, Navigator
 * patient filtering, VrnAtRiskReviewFormSideBar mount, at-risk-review lock
 * bypass) flows from this flag, so gating here is sufficient to gate the
 * whole experience. See documentation/VRN_HOMEHEALTH_AT_RISK_REVIEW.md.
 *
 * NOTE ON HOSTNAME GATING: an earlier revision of this helper also required
 * the browser hostname to be in a small allowlist (localhost / staging)
 * while billing/reports/dashboards were being audited. That host gate has
 * been lifted — activation is now controlled by role + clinic-type only.
 * If we ever need to ring-fence the feature to a subset of environments
 * again, restore the `isVrnHomeHealthHostAllowed()` check in front of the
 * existing conditions below; the docs (and the BE audit checklist in
 * documentation/VRN_HOMEHEALTH_AT_RISK_REVIEW.md) describe the previous
 * blocklist/allowlist shape.
 *
 * IMPORTANT: callers must read `user` and `clinic` from Redux selectors so the
 * check re-evaluates on clinic switch. Do not memoize on mount.
 */
export const isHomeHealthAtRiskOnlyUser = ({
  user,
  clinic,
}: {
  user?: { groups?: UserGroup[] | null } | null;
  clinic?: { clinic_type?: string | null } | null;
}): boolean => {
  if (clinic?.clinic_type !== "homehealth") return false;
  const groups = user?.groups ?? [];
  return groups.includes("setup_app_user") && groups.includes("VRN");
};

export const canBypassAtRiskReviewLock = ({
  userEmail,
  betaFeaturesAllowedList,
  user,
  clinic,
}: {
  userEmail?: string | null;
  betaFeaturesAllowedList?: string[];
  user?: { groups?: UserGroup[] | null } | null;
  clinic?: { clinic_type?: string | null } | null;
}): boolean => {
  // VRN users on home-health clinics bypass the at-risk-review lock entirely.
  if (isHomeHealthAtRiskOnlyUser({ user, clinic })) return true;

  const normalizedEmail = normalizeEmail(userEmail);
  if (!normalizedEmail) return false;

  const hasBypassFeature = betaFeaturesAllowedList?.includes(
    AT_RISK_REVIEW_LOCK_BYPASS_FEATURE
  );
  if (hasBypassFeature) return true;

  return featureFlag.atRiskReviewLockBypassEmailList.some(
    (email) => normalizeEmail(email) === normalizedEmail
  );
};

const getSavedPrechartingNote = (
  patient: INavigatorPatientsList | undefined
): string | null => {
  if (!patient) return null;

  const preChartNote = patient.pre_chart_note?.apptNote;
  if (typeof preChartNote === "string" && preChartNote.trim()) {
    return preChartNote;
  }

  if (!patient.extra_info) return null;

  try {
    const parsed = JSON.parse(patient.extra_info);
    const extraInfoNote = parsed.precharting_note;
    return typeof extraInfoNote === "string" && extraInfoNote.trim()
      ? extraInfoNote
      : null;
  } catch {
    return null;
  }
};

/**
 * Decorate the outstanding-patient entries returned by
 * `/at-risk-review-lock-status` with the bits the override modal actually
 * needs to show (display name + consent status). The lock endpoint itself
 * only returns patient_id + has_valid_reason + reason + expires_at, so we
 * look each entry up in the facility patient list (same payload that powers
 * the Navigator page) and attach display metadata plus the saved note.
 *
 * Any entry whose id is missing from `allPatients` falls through with both
 * fields undefined / null so the modal renders "Patient #<id>" as a safe
 * fallback instead of blocking the NP.
 */
export const enrichOutstandingPatients = (
  outstanding: IAtRiskReviewOutstandingPatient[],
  allPatients: INavigatorPatientsList[]
): IAtRiskOverridePatient[] =>
  outstanding.map((entry) => {
    const match = allPatients.find((p) => p.patient_id === entry.patient_id);
    const patientName = match
      ? match.last_name
        ? `${match.last_name}, ${match.first_name}`
        : match.name || undefined
      : undefined;

    return {
      ...entry,
      patient_name: patientName,
      consented_status: match?.consented_status ?? null,
      precharting_note: getSavedPrechartingNote(match),
    };
  });

/**
 * Consent statuses 0 (Denied) and 3 (Archived) are auto-prefillable by the
 * modal (they map to the "Denied consent" / "Patient is archived" preset
 * reasons). If every outstanding patient has one of those statuses, there is
 * nothing meaningful for the NP to pick and we can skip the modal entirely
 * and let navigation through. Anything else — including an undefined status
 * (patient not found in the facility list) — counts as non-prefillable and
 * means the modal still has to open.
 */
export const hasNonPrefillableOutstanding = (
  enriched: IAtRiskOverridePatient[]
): boolean =>
  enriched.some((p) => p.consented_status !== 0 && p.consented_status !== 3);

/**
 * After a partial save the lock endpoint is re-queried to reflect newly
 * covered patients. That response still lacks names, so we merge `patient_name`
 * and `consented_status` from the list we already have in component state
 * rather than re-fetching the heavy navigator payload.
 */
export const mergeEnrichmentFromExisting = (
  outstanding: IAtRiskReviewOutstandingPatient[],
  existing: IAtRiskOverridePatient[]
): IAtRiskOverridePatient[] => {
  const byId = new Map(existing.map((p) => [p.patient_id, p]));
  return outstanding.map((entry) => {
    const prev = byId.get(entry.patient_id);
    return {
      ...entry,
      patient_name: prev?.patient_name,
      consented_status: prev?.consented_status ?? null,
      precharting_note: prev?.precharting_note ?? null,
    };
  });
};
